
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface PerformanceAnalysis {
  overallAverage: number;
  subjectPerformance: Record<string, number>;
  categoryPerformance: Record<string, number>;
  topicPerformance: Record<string, number>;
  weakAreas: string[];
  strongAreas: string[];
  recentTrend: 'improving' | 'declining' | 'stable';
  recommendedFocus: string[];
  totalSessions: number;
  lastSessionDate: string | null;
}

export const useUserPerformance = () => {
  const { user } = useAuth();
  const [performance, setPerformance] = useState<PerformanceAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const calculatePerformanceAnalysis = async () => {
    if (!user) return null;

    setIsLoading(true);
    try {
      // Get test sessions with detailed information
      const { data: testSessions, error: testError } = await supabase
        .from('test_sessions')
        .select(`
          *,
          subjects(name, grade),
          test_answers(
            *,
            questions(
              *,
              subjects_categories(name),
              topics(title)
            )
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (testError) throw testError;

      if (!testSessions || testSessions.length === 0) {
        return {
          overallAverage: 0,
          subjectPerformance: {},
          categoryPerformance: {},
          topicPerformance: {},
          weakAreas: [],
          strongAreas: [],
          recentTrend: 'stable' as const,
          recommendedFocus: [],
          totalSessions: 0,
          lastSessionDate: null
        };
      }

      // Calculate overall average
      const completedSessions = testSessions.filter(s => s.completed && s.score !== null);
      const overallAverage = completedSessions.length > 0 
        ? completedSessions.reduce((sum, session) => sum + (session.score || 0), 0) / completedSessions.length 
        : 0;

      // Calculate subject performance
      const subjectScores: Record<string, number[]> = {};
      completedSessions.forEach(session => {
        const subjectName = session.subjects?.name || 'Unknown';
        if (!subjectScores[subjectName]) {
          subjectScores[subjectName] = [];
        }
        subjectScores[subjectName].push(session.score || 0);
      });

      // Average subject scores
      const subjectPerformance: Record<string, number> = {};
      Object.keys(subjectScores).forEach(subject => {
        const scores = subjectScores[subject];
        subjectPerformance[subject] = scores.reduce((sum, score) => sum + score, 0) / scores.length;
      });

      // Calculate category and topic performance
      const categoryStats: Record<string, { correct: number; total: number }> = {};
      const topicStats: Record<string, { correct: number; total: number }> = {};

      testSessions.forEach(session => {
        if (session.test_answers) {
          session.test_answers.forEach(answer => {
            const categoryName = answer.questions?.subjects_categories?.name;
            const topicTitle = answer.questions?.topics?.title;
            
            if (categoryName) {
              if (!categoryStats[categoryName]) {
                categoryStats[categoryName] = { correct: 0, total: 0 };
              }
              categoryStats[categoryName].total++;
              if (answer.is_correct) {
                categoryStats[categoryName].correct++;
              }
            }

            if (topicTitle) {
              if (!topicStats[topicTitle]) {
                topicStats[topicTitle] = { correct: 0, total: 0 };
              }
              topicStats[topicTitle].total++;
              if (answer.is_correct) {
                topicStats[topicTitle].correct++;
              }
            }
          });
        }
      });

      // Convert to percentages
      const categoryPerformance: Record<string, number> = {};
      Object.keys(categoryStats).forEach(category => {
        const data = categoryStats[category];
        categoryPerformance[category] = (data.correct / data.total) * 100;
      });

      const topicPerformance: Record<string, number> = {};
      Object.keys(topicStats).forEach(topic => {
        const data = topicStats[topic];
        topicPerformance[topic] = (data.correct / data.total) * 100;
      });

      // Identify weak and strong areas
      const allAreas = { ...categoryPerformance, ...topicPerformance };
      const weakAreas = Object.keys(allAreas).filter(area => allAreas[area] < 60);
      const strongAreas = Object.keys(allAreas).filter(area => allAreas[area] >= 80);

      // Calculate recent trend (last 5 vs previous 5 sessions)
      let recentTrend: 'improving' | 'declining' | 'stable' = 'stable';
      if (completedSessions.length >= 4) {
        const recent = completedSessions.slice(0, Math.min(5, completedSessions.length));
        const previous = completedSessions.slice(Math.min(5, completedSessions.length), Math.min(10, completedSessions.length));
        
        if (previous.length > 0) {
          const recentAvg = recent.reduce((sum, s) => sum + (s.score || 0), 0) / recent.length;
          const previousAvg = previous.reduce((sum, s) => sum + (s.score || 0), 0) / previous.length;
          
          if (recentAvg > previousAvg + 5) {
            recentTrend = 'improving';
          } else if (recentAvg < previousAvg - 5) {
            recentTrend = 'declining';
          }
        }
      }

      // Generate recommended focus areas
      const recommendedFocus = weakAreas.slice(0, 3);

      const analysis: PerformanceAnalysis = {
        overallAverage,
        subjectPerformance,
        categoryPerformance,
        topicPerformance,
        weakAreas,
        strongAreas,
        recentTrend,
        recommendedFocus,
        totalSessions: completedSessions.length,
        lastSessionDate: completedSessions.length > 0 ? completedSessions[0].created_at : null
      };

      setPerformance(analysis);
      return analysis;

    } catch (error) {
      console.error('Error calculating performance analysis:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      calculatePerformanceAnalysis();
    }
  }, [user]);

  return {
    performance,
    isLoading,
    refreshPerformance: calculatePerformanceAnalysis
  };
};
